import { useState, useEffect } from 'react';

/**
 * How long a revealed password stays visible before auto-hiding.
 * Hard timer — does NOT reset on activity. Mirrors common password
 * managers (1Password, Bitwarden) where the reveal-window is a fixed
 * shoulder-surfing guard rather than an idle-out behavior.
 */
const PASSWORD_REVEAL_MS = 60 * 1000;
import { useNavigate, useParams } from 'react-router-dom';
import { useSafe } from '../context/SafeContext';
import LoadingPane from '../components/LoadingPane';
import { listCircles } from '../api/client';
import { HbsSiteIcon } from '../components/hbs/SiteIcon';
import { HbsInput } from '../components/hbs/Input';
import { HbsButton } from '../components/hbs/Button';
import { Icon } from '../components/hbs/Icon';
import { EyeToggle } from '../components/hbs/EyeToggle';
import { SecretValue } from '../components/hbs/SecretValue';
import TotpCodeDisplay from '../components/TotpCodeDisplay';
import { ShareWithFamilyAction } from '../components/ShareWithFamilyAction';
import { domainFromUrl } from '../utils/domainFromUrl';
import { candidateFaviconUrls, registrableDomain } from '../utils/faviconUrl';
import { effectiveWebIdentifiers, ensureUrlScheme } from '@heybesafe/shared';
import type { Circle, PasswordHistoryEntry } from '@heybesafe/shared';

/**
 * Human-readable "updated …" label. `updatedAt` is an ISO 8601 string
 * (VaultItem.updatedAt); returns '' for a missing/unparseable date so the
 * caller can omit the label entirely rather than render "NaN".
 */
function relativeUpdatedAt(updatedAt: string): string {
  const updatedMs = new Date(updatedAt).getTime();
  if (Number.isNaN(updatedMs)) return '';
  const ms = Date.now() - updatedMs;
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'updated today';
  if (days === 1) return 'updated yesterday';
  if (days < 30) return `updated ${days} days ago`;
  if (days < 365) return `updated ${Math.floor(days / 30)} months ago`;
  return `updated ${Math.floor(days / 365)} years ago`;
}

function CopyAction({ value, label, onCopied }: { value: string; label: string; onCopied?: () => void }): JSX.Element {
  return (
    <button
      // One CopyAction per field, so the field label (a fixed internal string
      // such as "username" or "password") namespaces the id.
      id={`item-copy-${label.toLowerCase().replace(/\s+/g, '-')}-button`}
      type="button"
      aria-label={`Copy ${label}`}
      // Visible hover tooltip (native `title`, the codebase's tooltip
      // convention) — e.g. "Copy username" / "Copy password".
      title={`Copy ${label}`}
      onClick={async () => {
        await navigator.clipboard.writeText(value);
        onCopied?.();
      }}
      className="p-1 rounded-hbs-sm text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt"
    >
      <Icon name="copy" size={16} />
    </button>
  );
}

function CustomFieldDetailRow({ name, value, hidden }: { name: string; value: string; hidden?: boolean }): JSX.Element {
  const [revealed, setRevealed] = useState(false);
  const masked = !!hidden && !revealed;
  return (
    <HbsInput
      label={name}
      value={value}
      onChange={() => {}}
      readOnly
      type={masked ? 'password' : 'text'}
      mono={!masked && !!hidden}
      renderValue={!masked && hidden ? (v) => <SecretValue value={v} /> : undefined}
      trailingActions={
        <>
          {hidden && <EyeToggle revealed={revealed} onChange={setRevealed} />}
          <CopyAction value={value} label={name} />
        </>
      }
    />
  );
}

// "Used until <date>" label for a retired password. Falls back to the raw
// timestamp if it can't be parsed.
function formatReplacedAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function PreviousPasswordRow({ entry }: { entry: PasswordHistoryEntry }): JSX.Element {
  const [revealed, setRevealed] = useState(false);
  const usedUntil = formatReplacedAt(entry.replacedAt);
  return (
    <HbsInput
      label={`Used until ${usedUntil}`}
      value={entry.password}
      onChange={() => {}}
      readOnly
      type={revealed ? 'text' : 'password'}
      mono={revealed}
      renderValue={revealed ? (v) => <SecretValue value={v} /> : undefined}
      trailingActions={
        <>
          <EyeToggle revealed={revealed} onChange={setRevealed} label={`used until ${usedUntil}`} />
          <CopyAction value={entry.password} label={`previous password used until ${usedUntil}`} />
        </>
      }
    />
  );
}

/** Collapsed-by-default list of an item's previous passwords, newest first. */
function PreviousPasswords({ history }: { history: PasswordHistoryEntry[] }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (history.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      <button
        id="item-previous-passwords-toggle"
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="self-start flex items-center gap-1.5 text-[12px] font-semibold text-hbs-text-mute hover:text-hbs-text"
      >
        <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {open ? 'Hide previous passwords' : `Show previous passwords (${history.length})`}
      </button>
      {open && (
        <div className="flex flex-col gap-3">
          {history.map((entry, i) => (
            <PreviousPasswordRow key={`${entry.replacedAt}-${i}`} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ItemDetailPage(): JSX.Element {
  // Route can be either `/safe/:id` (personal) or `/orgs/:orgId/items/:itemId`
  // (family-shared). React Router only supplies the params declared by the
  // active route; `orgId` is undefined on the personal route. We disambiguate
  // the item lookup so that an id collision across partitions cannot pull
  // back a personal item when the user is on a family route, and vice versa.
  const params = useParams<{ id?: string; orgId?: string; itemId?: string }>();
  const orgId = params.orgId;
  const id = orgId ? params.itemId : params.id;
  const navigate = useNavigate();
  const { items, itemsLoaded, deleteItem, updateItem, orgs } = useSafe();
  const [revealed, setRevealed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Circle name for the "Shared with" label — resolved lazily when a shared
  // item carries a circleId (plaintext server metadata threaded through sync).
  const [circleNameMap, setCircleNameMap] = useState<Map<string, string>>(new Map());

  // Auto-hide the password 60s after reveal. Hard timer (no activity reset).
  useEffect(() => {
    if (!revealed) return;
    const t = setTimeout(() => setRevealed(false), PASSWORD_REVEAL_MS);
    return () => clearTimeout(t);
  }, [revealed]);

  // Fetch the org's circles once per orgId so we can resolve circleId → name
  // for the "Shared with" label. Runs whenever the route's orgId changes.
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    listCircles(orgId).then((r) => {
      if (!cancelled && r.ok) {
        const map = new Map<string, string>(
          r.data.circles.map((c: Circle) => [c.id, c.name]),
        );
        setCircleNameMap(map);
      }
    });
    return () => { cancelled = true; };
  }, [orgId]);

  const item = items.find((i) =>
    orgId ? i.id === id && i.orgId === orgId : i.id === id && !i.orgId,
  );

  if (!item) {
    // items is emptied on lock and only repopulated by the post-unlock
    // syncPull, so an absent item means loading until itemsLoaded.
    if (!itemsLoaded) return <LoadingPane label="Loading item…" />;
    return (
      <div className="h-full flex flex-col items-center justify-center text-hbs-text-mute gap-3">
        <p className="text-[14px]">Item not found.</p>
        <HbsButton variant="plain" id="item-back-to-safe-button" onClick={() => navigate('/safe')}>
          Back to safe
        </HbsButton>
      </div>
    );
  }

  const isPasskey = item.type === 'passkey';
  // For passkey items we prefer rpName/rpId for the header; the SiteIcon falls
  // back to the rpId for favicon resolution. Login items derive the domain
  // from `url` as they always have.
  const iconHost = domainFromUrl(item.url, '');
  const domain = isPasskey
    ? (item.rpId ?? item.rpName ?? item.name)
    : (iconHost ? registrableDomain(iconHost) : item.name);
  const updatedLabel = relativeUpdatedAt(item.updatedAt);
  const isSharedItem = !!item.orgId;
  // Personal items: the viewer is the owner, so they have full rights.
  // Shared items: any member of the owning family may edit; only an admin
  // may unshare (delete).
  const itemOrg = isSharedItem ? orgs.find((o) => o.id === item.orgId) : undefined;
  const canEdit = isSharedItem ? !!itemOrg : true;
  const canDelete = isSharedItem ? itemOrg?.role === 'admin' : true;
  const editPath = isSharedItem
    ? `/orgs/${item.orgId}/items/${item.id}/edit`
    : `/safe/${item.id}/edit`;

  // "Shared with" label — shown on shared items. circleId is plaintext server
  // metadata threaded through syncPull; absent means the whole family can see it.
  const sharedWithLabel = isSharedItem
    ? item.circleId
      ? `Shared with ${circleNameMap.get(item.circleId) ?? item.circleId}`
      : 'Shared with Everyone'
    : null;

  async function handleDelete(): Promise<void> {
    setDeleting(true);
    try {
      await deleteItem(item!.id);
      // Shared-item Unshare returns the admin to the family's detail page;
      // personal Delete returns to the personal safe.
      navigate(item!.orgId ? `/orgs/${item!.orgId}` : '/safe');
    } finally {
      setDeleting(false);
    }
  }

  // Modern items store the launch URL in identifiers[]; legacy items use
  // the bare `url` field. ensureUrlScheme tolerates bare-hostname inputs.
  const launchHref = ensureUrlScheme(effectiveWebIdentifiers(item!)[0] ?? item!.url ?? '');

  function handleLaunch(): void {
    if (!launchHref) return;
    window.open(launchHref, '_blank', 'noopener,noreferrer');
  }

  async function handleDeletePasskey(idx: number): Promise<void> {
    if (!item!.passkeys) return;
    const next = item!.passkeys.filter((_, i) => i !== idx);
    await updateItem(item!.id, {
      passkeys: next.length > 0 ? next : undefined,
    });
  }

  return (
    <div className="p-7 flex flex-col gap-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-start gap-4">
        <HbsSiteIcon domain={domain} faviconUrls={candidateFaviconUrls(item)} size={64} />
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-semibold text-[30px] tracking-hbs-tighter text-hbs-text truncate">
            {domain}
          </h2>
          <p className="text-[13px] text-hbs-text-mute mt-1">
            {isPasskey ? 'Passkey' : item.type === 'card' ? 'Payment Card' : 'Login'}{updatedLabel && ` · ${updatedLabel}`}
          </p>
          {sharedWithLabel && (
            <p className="text-[12px] text-hbs-text-mute mt-0.5">
              {sharedWithLabel}
            </p>
          )}
        </div>
        {!isPasskey && (
          <div className="flex items-center gap-2">
            <HbsButton
              id="item-launch-autofill-button"
              variant="primary"
              leadingIcon={<Icon name="launch" size={14} />}
              disabled={!launchHref}
              onClick={handleLaunch}
            >
              Launch &amp; autofill
            </HbsButton>
          </div>
        )}
      </div>

      {/* Credentials card */}
      <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6">
        <div className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-4">
          {isPasskey ? 'Passkey' : 'Credentials'}
        </div>
        {isPasskey ? (
          <div className="flex flex-col gap-4">
            <div>
              <HbsInput
                label="Site"
                value={item.rpName ?? item.rpId ?? ''}
                onChange={() => {}}
                readOnly
              />
              {item.rpId && item.rpId !== item.rpName && (
                <p className="mt-1 text-[12px] text-hbs-text-mute">{item.rpId}</p>
              )}
            </div>
            <div>
              <HbsInput
                label="Username"
                value={item.userName ?? ''}
                onChange={() => {}}
                readOnly
              />
              {item.userDisplayName && item.userDisplayName !== item.userName && (
                <p className="mt-1 text-[12px] text-hbs-text-mute">{item.userDisplayName}</p>
              )}
            </div>
            <p className="text-[12px] text-hbs-text-mute">
              Created {new Date(item.createdAt).toLocaleDateString()}
            </p>
            {item.lastUsedAt && (
              <p className="text-[12px] text-hbs-text-mute">
                Last used {new Date(item.lastUsedAt).toLocaleDateString()}
              </p>
            )}
            {/* Deleting a passkey here only removes the user's local copy of the
                credential — the relying party still has the public key bound to
                the credentialId. The popup ItemDetail shows the same warning. */}
            <p className="text-[12px] text-hbs-warning bg-hbs-warning/10 border border-hbs-warning/20 rounded-hbs-sm px-3 py-2">
              Deleting here only removes your local copy. Visit the site to remove it from your account there.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {item.username && (
              <HbsInput
                label="Username"
                value={item.username}
                onChange={() => {}}
                readOnly
                trailingActions={<CopyAction value={item.username} label="username" />}
              />
            )}
            {item.password && (
              <HbsInput
                label="Password"
                value={item.password}
                onChange={() => {}}
                readOnly
                type={revealed ? 'text' : 'password'}
                mono={revealed}
                renderValue={revealed ? (v) => <SecretValue value={v} /> : undefined}
                trailingActions={
                  <>
                    <EyeToggle revealed={revealed} onChange={setRevealed} />
                    <CopyAction value={item.password} label="password" />
                  </>
                }
              />
            )}
            {item.passwordHistory && item.passwordHistory.length > 0 && (
              <PreviousPasswords history={item.passwordHistory} />
            )}
            {item.totp && (
              <TotpCodeDisplay
                totp={item.totp}
              />
            )}
            {item.url && (
              <HbsInput
                label="Website"
                value={item.url}
                onChange={() => {}}
                readOnly
                trailingActions={
                  <>
                    <button
                      id="item-open-website-button"
                      type="button"
                      aria-label="Open website"
                      onClick={handleLaunch}
                      className="p-1 rounded-hbs-sm text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt"
                    >
                      <Icon name="launch" size={16} />
                    </button>
                    <CopyAction value={item.url} label="URL" />
                  </>
                }
              />
            )}
            {item.customFields && item.customFields.length > 0 && (
              <>
                {item.customFields.map((field, idx) => (
                  <CustomFieldDetailRow
                    key={`${field.name}-${idx}`}
                    name={field.name}
                    value={field.value}
                    hidden={field.hidden}
                  />
                ))}
              </>
            )}
            {item.content && (
              <HbsInput
                label="Notes"
                value={item.content}
                onChange={() => {}}
                readOnly
                trailingActions={<CopyAction value={item.content} label="notes" />}
              />
            )}
            {item.passkeys && item.passkeys.length > 0 && (
              <div>
                <div className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-3">
                  Passkeys
                </div>
                <div className="flex flex-col gap-2">
                  {item.passkeys.map((pk, idx) => (
                    <div
                      key={pk.credentialId}
                      className="flex items-center gap-3 bg-hbs-surface border border-hbs-border rounded-hbs-lg px-4 py-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[14px] text-hbs-text truncate">
                          {pk.userName || pk.userDisplayName}
                        </div>
                        <div className="text-[12px] text-hbs-text-mute mt-0.5">
                          Created {new Date(pk.createdAt).toLocaleDateString()}
                          {pk.lastUsedAt && ` · Last used ${new Date(pk.lastUsedAt).toLocaleDateString()}`}
                        </div>
                      </div>
                      {canEdit && (
                        <button
                          type="button"
                          id={`item-passkey-remove-${idx}`}
                          aria-label="Remove this passkey"
                          onClick={() => void handleDeletePasskey(idx)}
                          className="p-1 rounded-hbs-sm text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt"
                        >
                          <svg
                            width={16}
                            height={16}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ flexShrink: 0 }}
                          >
                            <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <div className="mt-6 flex justify-between items-center">
          <div className="flex items-center gap-2">
            {canEdit && (
              <HbsButton variant="plain" id="item-edit-button" onClick={() => navigate(editPath)}>
                Edit
              </HbsButton>
            )}
            {/* ShareWithFamilyAction is self-gating (hides when item.orgId is
                set or user has no families) but we also skip rendering it for
                shared items to keep the action row clean.
                key={item.id} forces React to remount the action when the user
                navigates between items on the same /safe/:id route — without
                it, the component's local `lastSharedFamilyName` state would
                leak the "Shared with [family]" confirmation onto the next
                item the user viewed. */}
            {!isSharedItem && !isPasskey && <ShareWithFamilyAction key={item.id} item={item} />}
          </div>
          {canDelete &&
            (!confirming ? (
              <HbsButton variant="plain" id="item-delete-button" onClick={() => setConfirming(true)}>
                {isSharedItem ? 'Unshare' : 'Delete'}
              </HbsButton>
            ) : (
              <div className="flex gap-2">
                <HbsButton variant="ghost" id="item-delete-cancel-button" onClick={() => setConfirming(false)}>
                  Cancel
                </HbsButton>
                <HbsButton variant="accent" loading={deleting} id="item-delete-confirm-button" onClick={handleDelete}>
                  Confirm
                </HbsButton>
              </div>
            ))}
        </div>
      </section>
    </div>
  );
}
